import React, { useState } from "react";
import {
  Languages,
  Image,
  FileText,
  Calendar,
  ArrowRight,
  Upload,
  Copy,
  Download,
  Trash2,
  AlertCircle,
  CheckCircle,
  Clock,
  Zap,
  Brain,
  Gauge,
  ChevronDown,
  X,
  RefreshCw,
} from "lucide-react";
import Section1 from "../assets/section1.png";
import Section2 from "../assets/section2.png";
import Section3 from "../assets/section3.png";
import Section4 from "../assets/section4.png";
import Section5 from "../assets/section5.png";
import Section6 from "../assets/section6.png";
import Section7 from "../assets/section7.png";
import Section8 from "../assets/section8.png";
import Section9 from "../assets/section9.png";
import Section10 from "../assets/section10.png";
import Section11 from "../assets/section11.png";
import Section12 from "../assets/section12.png";
const UsageTranslate = () => {
  const [activeTab, setActiveTab] = useState("instant");
  const [open, setOpen] = useState(false);
  const TabButton = ({ id, icon: Icon, label, active, onClick }) => (
    <button
      onClick={onClick}
      className={`flex items-center gap-2 px-6 py-3 rounded-full font-medium transition-all duration-300 ${
        active
          ? "bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-lg"
          : "bg-gray-100 text-gray-600 hover:bg-gray-200"
      }`}
    >
      <Icon className="w-5 h-5" />
      {label}
    </button>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-50">
      {/* Hero Section */}
      <section className="relative overflow-hidden py-20">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-600/10 via-purple-600/10 to-blue-600/10"></div>
        <div className="relative container mx-auto px-6">
          <div className="text-center space-y-6">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-100 rounded-full text-blue-700 text-sm font-medium">
              <Languages className="w-4 h-4" />
              Advanced Translation System
            </div>

            <h1 className="text-5xl lg:text-6xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
              TGL 번역 시스템
            </h1>

            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Elva의 고급 번역 시스템은 TGL(Translated-Generated-Language
              Model)을 통해 사람처럼 자연스러운 번역을 제공하며, 원문의 문맥을
              최대한 유지합니다.
            </p>

            <div className="flex flex-wrap justify-center gap-4 mt-8">
              <div className="flex items-center gap-2 bg-white px-4 py-2 rounded-full shadow-md">
                <span className="text-2xl">🇰🇷</span>
                <span className="text-gray-700">한국어</span>
              </div>
              <div className="flex items-center gap-2 bg-white px-4 py-2 rounded-full shadow-md">
                <span className="text-2xl">🇨🇳</span>
                <span className="text-gray-700">중국어</span>
              </div>
              <div className="flex items-center gap-2 bg-white px-4 py-2 rounded-full shadow-md">
                <span className="text-2xl">🇺🇸</span>
                <span className="text-gray-700">영어</span>
              </div>
              <div className="flex items-center gap-2 bg-white px-4 py-2 rounded-full shadow-md">
                <span className="text-2xl">🇯🇵</span>
                <span className="text-gray-700">일본어</span>
              </div>
              <div className="flex items-center gap-2 bg-white px-4 py-2 rounded-full shadow-md">
                <span className="text-2xl">🇻🇳</span>
                <span className="text-gray-700">베트남어</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Tab Navigation */}
      <section className="sticky top-0 z-10 bg-white/80 backdrop-blur-lg border-b border-gray-200">
        <div className="container mx-auto px-6 py-4">
          <div className="flex flex-wrap justify-center gap-4">
            <TabButton
              id="instant"
              icon={Zap}
              label="즉시 번역"
              active={activeTab === "instant"}
              onClick={() => setActiveTab("instant")}
            />
            <TabButton
              id="image"
              icon={Image}
              label="이미지 번역"
              active={activeTab === "image"}
              onClick={() => setActiveTab("image")}
            />
            <TabButton
              id="schedule"
              icon={Calendar}
              label="스케줄 번역"
              active={activeTab === "schedule"}
              onClick={() => setActiveTab("schedule")}
            />
          </div>
        </div>
      </section>

      {/* Content Sections */}
      <section className="py-16">
        <div className="container mx-auto px-6">
          {/* Section 1: Instant Translation */}
          {activeTab === "instant" && (
            <div className="max-w-4xl mx-auto space-y-12 animate-fadeIn">
              <div className="text-center space-y-4">
                <h2 className="text-3xl font-bold text-gray-800">
                  기능 1. 즉시 번역
                </h2>
                <p className="text-gray-600 text-lg">
                  단순 텍스트를 빠르게 번역해주는 기능으로 별도의 파일 없이 바로
                  번역이 가능합니다.
                </p>
              </div>

              {/* Step 1 */}
              <div className="bg-white rounded-2xl shadow-xl p-8 space-y-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                    <span className="text-blue-600 font-bold">1</span>
                  </div>
                  <h3 className="text-xl font-semibold text-gray-800">
                    텍스트 입력
                  </h3>
                </div>
                <img
                  src={Section1}
                  alt="텍스트 입력 화면"
                  className="w-full rounded-lg shadow-md"
                />
                <p className="text-gray-600">
                  번역하고자 하는 텍스트를 입력창에 입력하세요.
                </p>
              </div>

              {/* Step 2 */}
              <div className="bg-white rounded-2xl shadow-xl p-8 space-y-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                    <span className="text-purple-600 font-bold">2</span>
                  </div>
                  <h3 className="text-xl font-semibold text-gray-800">
                    언어 자동 감지
                  </h3>
                </div>
                <img
                  src={Section2}
                  alt="언어 자동 감지"
                  className="w-full rounded-lg shadow-md"
                />
                <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                  <p className="text-gray-700">
                    <strong>TGL은 입력된 텍스트의 언어를 자동으로 감지</strong>
                    하므로, 번역하고자 하는 대상 언어만 선택해주시면 됩니다.
                  </p>
                </div>
              </div>

              {/* Step 3 */}
              <div className="bg-white rounded-2xl shadow-xl p-8 space-y-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                    <span className="text-green-600 font-bold">3</span>
                  </div>
                  <h3 className="text-xl font-semibold text-gray-800">
                    번역 결과 확인
                  </h3>
                </div>
                <img
                  src={Section3}
                  alt="번역 결과"
                  className="w-full rounded-lg shadow-md"
                />
                <p className="text-gray-600">
                  아래와 같이 번역된 결과를 확인할 수 있습니다.
                </p>
              </div>
            </div>
          )}

          {/* Section 2: Image Translation */}
          {activeTab === "image" && (
            <div className="max-w-4xl mx-auto space-y-12 animate-fadeIn">
              <div className="text-center space-y-4">
                <h2 className="text-3xl font-bold text-gray-800">
                  기능 2. 이미지 번역
                </h2>
                <p className="text-gray-600 text-lg">
                  스캔본이나 이미지 파일 내부에 있는 텍스트를 번역할 때
                  사용됩니다.
                </p>
                <div className="bg-amber-50 p-4 rounded-lg border border-amber-200 max-w-2xl mx-auto">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5" />
                    <div className="text-left">
                      <p className="text-sm text-gray-700">
                        • 한 번에 최대 <strong>100장</strong>까지 동시 번역 가능
                        <br />
                        • 이미지 수에 따라 번역 시간이 증가합니다
                        <br />• 번역 중 페이지 이동 시 작업이 취소됩니다
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Step 1 */}
              <div className="bg-white rounded-2xl shadow-xl p-8 space-y-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                    <span className="text-blue-600 font-bold">1</span>
                  </div>
                  <h3 className="text-xl font-semibold text-gray-800">
                    이미지 업로드
                  </h3>
                </div>
                <img
                  src={Section4}
                  alt="이미지 업로드"
                  className="w-full rounded-lg shadow-md"
                />
                <p className="text-gray-600">
                  + 버튼을 누르거나 드래그 앤 드롭으로 이미지를 업로드하세요.
                </p>
              </div>

              {/* Step 2 */}
              <div className="bg-white rounded-2xl shadow-xl p-8 space-y-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                    <span className="text-purple-600 font-bold">2</span>
                  </div>
                  <h3 className="text-xl font-semibold text-gray-800">
                    이미지 관리
                  </h3>
                </div>
                <img
                  src={Section5}
                  alt="이미지 관리"
                  className="w-full rounded-lg shadow-md"
                />
                <p className="text-sm text-gray-700 leading-relaxed">
                  이미지를 업로드하면&nbsp;
                  <span className="font-medium">위와 같은 썸네일</span>이
                  표시됩니다.
                  <br />
                  잘못 올린 이미지는{" "}
                  <span className="text-red-600 font-semibold">
                    붉은색 ✕ 버튼
                  </span>
                  으로 삭제할 수 있고,
                  <br />
                  모든 이미지를 한 번에 지우려면&nbsp;
                  <span className="text-green-600 font-semibold">
                    [전체 삭제]
                  </span>{" "}
                  버튼을 눌러 주세요.
                </p>
              </div>

              {/* Step 3 */}
              <div className="bg-white rounded-2xl shadow-xl p-8 space-y-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                    <span className="text-green-600 font-bold">3</span>
                  </div>
                  <h3 className="text-xl font-semibold text-gray-800">
                    번역 완료
                  </h3>
                </div>
                <img
                  src={Section6}
                  alt="번역 완료"
                  className="w-full rounded-lg shadow-md"
                />
                <p className="text-sm text-gray-700 leading-relaxed">
                  번역이 완료되면&nbsp;
                  <span className="font-medium">우측의 “전체 복사” 버튼</span>
                  으로 결과를 복사할 수 있습니다.
                  <br />
                  추가 번역이 필요하시면&nbsp;
                  <span className="font-medium">“다시 업로드”</span> 버튼을 눌러
                  초기 화면으로 돌아가세요.
                </p>
              </div>
            </div>
          )}

          {/* Section 3: Schedule Translation */}
          {activeTab === "schedule" && (
            <div className="max-w-4xl mx-auto space-y-12 animate-fadeIn">
              <div className="text-center space-y-4">
                <h2 className="text-3xl font-bold text-gray-800">
                  기능 3. 스케줄 등록을 통한 번역
                </h2>
                <p className="text-gray-600 text-lg">
                  PDF와 같은 대용량 파일은 스케줄을 통해 처리할 수 있습니다.
                </p>
                <div className="bg-blue-50 p-4 rounded-lg border border-blue-200 max-w-2xl mx-auto">
                  <p className="text-sm text-gray-700">
                    현재 한 번에 최대 <strong>500페이지</strong>의 PDF를 처리할
                    수 있습니다.
                  </p>
                </div>
                <div className="bg-amber-50 p-4 rounded-lg border border-amber-200 max-w-2xl mx-auto">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5" />
                    <p className="text-sm text-gray-700 text-left">
                      <strong>제한사항:</strong> 서버 환경 제약으로 한 번에
                      5개의 파일만 스케줄 등록이 가능합니다.
                    </p>
                  </div>
                </div>
              </div>

              {/* Step 1 */}
              <div className="bg-white rounded-2xl shadow-xl p-8 space-y-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                    <span className="text-blue-600 font-bold">1</span>
                  </div>
                  <h3 className="text-xl font-semibold text-gray-800">
                    스케줄 등록
                  </h3>
                </div>
                <img
                  src={Section7}
                  alt="스케줄 등록"
                  className="w-full rounded-lg shadow-md"
                />
                <p className="text-gray-600">
                  + 버튼을 클릭하여 스케줄 등록 창을 엽니다.
                </p>
              </div>

              {/* Step 2 */}
              <div className="bg-white rounded-2xl shadow-xl p-8 space-y-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                    <span className="text-purple-600 font-bold">2</span>
                  </div>
                  <h3 className="text-xl font-semibold text-gray-800">
                    작업 설정
                  </h3>
                </div>
                <img
                  src={Section8}
                  alt="작업 설정"
                  className="w-full rounded-lg shadow-md"
                />
                <div className="space-y-3 mt-4">
                  <div className="flex items-start gap-3">
                    <FileText className="w-5 h-5 text-gray-600 mt-0.5" />
                    <div>
                      <p className="font-medium text-gray-800">작업 이름</p>
                      <p className="text-sm text-gray-600">
                        구분 가능한 작업명 입력
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Brain className="w-5 h-5 text-gray-600 mt-0.5" />
                    <div>
                      <p className="font-medium text-gray-800">모델 선택</p>
                      <p className="text-sm text-gray-600">
                        번역 품질에 따른 모델 선택
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Languages className="w-5 h-5 text-gray-600 mt-0.5" />
                    <div>
                      <p className="font-medium text-gray-800">대상 언어</p>
                      <p className="text-sm text-gray-600">
                        번역하고자 하는 언어 선택
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Model Selection */}
              <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-2xl p-8">
                <h3 className="text-xl font-semibold text-gray-800 mb-6">
                  모델 선택 가이드
                </h3>
                <div className="grid md:grid-cols-3 gap-6">
                  <div className="bg-white p-6 rounded-xl shadow-md hover:shadow-lg transition-shadow">
                    <div className="flex items-center gap-3 mb-3">
                      <Zap className="w-6 h-6 text-blue-600" />
                      <h4 className="font-semibold text-gray-800">TGL-8X</h4>
                    </div>
                    <p className="text-sm text-gray-600">
                      빠른 작업과 단순 대화문 번역에 적합
                    </p>
                    <div className="mt-3 flex items-center gap-2">
                      <Gauge className="w-4 h-4 text-gray-500" />
                      <span className="text-xs text-gray-500">
                        속도: ⚡⚡⚡
                      </span>
                    </div>
                  </div>

                  <div className="bg-white p-6 rounded-xl shadow-md hover:shadow-lg transition-shadow">
                    <div className="flex items-center gap-3 mb-3">
                      <Brain className="w-6 h-6 text-purple-600" />
                      <h4 className="font-semibold text-gray-800">TGL-23X</h4>
                    </div>
                    <p className="text-sm text-gray-600">
                      공문, 제안서 등 전문적인 번역에 적합
                    </p>
                    <div className="mt-3 flex items-center gap-2">
                      <Gauge className="w-4 h-4 text-gray-500" />
                      <span className="text-xs text-gray-500">속도: ⚡⚡</span>
                    </div>
                  </div>

                  <div className="bg-white p-6 rounded-xl shadow-md hover:shadow-lg transition-shadow">
                    <div className="flex items-center gap-3 mb-3">
                      <CheckCircle className="w-6 h-6 text-green-600" />
                      <h4 className="font-semibold text-gray-800">TGL-51X</h4>
                    </div>
                    <p className="text-sm text-gray-600">
                      최고 정확도의 전문 번역이 필요한 경우
                    </p>
                    <div className="mt-3 flex items-center gap-2">
                      <Gauge className="w-4 h-4 text-gray-500" />
                      <span className="text-xs text-gray-500">속도: ⚡</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Step 3 */}
              <div className="bg-white rounded-2xl shadow-xl p-8 space-y-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-yellow-100 rounded-full flex items-center justify-center">
                    <span className="text-yellow-600 font-bold">3</span>
                  </div>
                  <h3 className="text-xl font-semibold text-gray-800">
                    작업 진행 상태
                  </h3>
                </div>
                <img
                  src={Section10}
                  alt="작업 대기중"
                  className="w-full rounded-lg shadow-md"
                />
                <div className="p-4 bg-yellow-50 rounded-lg border border-yellow-200 space-y-1">
                  {/* 메인 안내 + 시계 아이콘 */}
                  <div className="flex items-center gap-2">
                    <Clock className="w-5 h-5 text-yellow-600 shrink-0" />
                    <span className="text-gray-700 text-sm">
                      작업이 완료되면 자동으로 완료 창으로 이동합니다.
                    </span>
                  </div>

                  {/* 추가 경고 문구 (빨간색) */}
                  <p className="text-xs text-red-600 pl-7">
                    ※ 인터넷이 불안정하거나 예상보다 오래 소요될 경우&nbsp;
                    <strong>페이지를 새로고침</strong>하여 진행 상태를 다시
                    확인해 주세요.
                  </p>
                </div>
              </div>

              {/* Step 4 */}
              <div className="bg-white rounded-2xl shadow-xl p-8 space-y-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                    <span className="text-green-600 font-bold">4</span>
                  </div>
                  <h3 className="text-xl font-semibold text-gray-800">
                    작업 완료
                  </h3>
                </div>
                <img
                  src={Section11}
                  alt="작업 완료"
                  className="w-full rounded-lg shadow-md"
                />
                <p className="text-sm text-gray-700 leading-relaxed">
                  작업이 완료되면&nbsp;
                  <span className="font-medium">
                    위와 같은 “작업 완료” 화면
                  </span>
                  으로 전환됩니다.
                  <br />
                  우측{" "}
                  <span className="text-red-500 font-semibold">빨간 박스</span>
                  의&nbsp;
                  <strong>다운로드&nbsp;·&nbsp;삭제</strong> 아이콘을 사용해
                  <br />
                  번역 결과를 저장하거나 삭제할 수 있습니다.
                  <br />
                  <span className="text-red-600 font-semibold">
                    삭제한 파일은&nbsp;Elva 데이터 처리 방침에 따라&nbsp;
                    <u>복구가 불가능하오니</u> 주의해 주세요.
                  </span>
                </p>
              </div>

              {/* Error State */}
              <div className="bg-white rounded-2xl shadow-xl p-8 space-y-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                    <X className="w-6 h-6 text-red-600" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-800">
                    번역 실패
                  </h3>
                </div>
                <img
                  src={Section12}
                  alt="번역 실패"
                  className="w-full rounded-lg shadow-md"
                />
                <div className="space-y-3">
                  <p className="text-gray-600">번역이 실패할 수 있는 경우:</p>
                  <ul className="space-y-2">
                    <li className="flex items-start gap-2">
                      <div className="w-2 h-2 bg-red-500 rounded-full mt-1.5"></div>
                      <span className="text-gray-700">파일 형식 오류</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <div className="w-2 h-2 bg-red-500 rounded-full mt-1.5"></div>
                      <span className="text-gray-700">서버 오류</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <div className="w-2 h-2 bg-red-500 rounded-full mt-1.5"></div>
                      <span className="text-gray-700">
                        윤리 정책 위반 내용 포함
                      </span>
                    </li>
                  </ul>
                  <p className="text-sm text-gray-600 mt-4">
                    지속적인 실패 시 담당자에게 문의해 주세요.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Footer CTA */}
      {/* <section className="py-16 bg-gradient-to-r from-blue-600 to-purple-600">
        <div className="container mx-auto px-6 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            더 자연스러운 번역을 경험하세요
          </h2>
          <p className="text-blue-100 mb-8">
            TGL과 함께 전문적이고 정확한 번역 서비스를 이용해보세요
          </p>
          <button className="bg-white text-blue-600 px-8 py-3 rounded-full font-semibold hover:bg-blue-50 transition-colors shadow-lg">
            번역 시작하기 <ArrowRight className="inline-block ml-2 w-5 h-5" />
          </button>
        </div>
      </section> */}
      {open && (
        <div
          onClick={() => setOpen(false)} // 배경 클릭해도 닫힘
          className="fixed inset-0 z-50 flex items-center justify-center
                     bg-black/60 backdrop-blur-sm"
        >
          <div
            onClick={(e) => e.stopPropagation()} // 이미지 클릭은 통과
            className="relative max-w-4xl w-full p-4"
          >
            <button
              onClick={() => setOpen(false)}
              className="absolute top-2 right-2 text-white text-2xl"
            >
              &times;
            </button>
            <img
              src={Section3}
              alt="미리보기"
              className="w-full h-auto rounded shadow-lg"
            />
          </div>
        </div>
      )}
      <style jsx>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .animate-fadeIn {
          animation: fadeIn 0.5s ease-out;
        }
      `}</style>
    </div>
  );
};

export default UsageTranslate;
